import { CloseRounded, Delete } from '@mui/icons-material';
import { Grid, TableContainer, Table, TableBody, TableCell, TableHead, TableRow, IconButton } from '@mui/material';
import React, { SyntheticEvent, useEffect, useState } from 'react';

import { BillOfMaterialsProps } from './BillOfMaterials.props';
import { StyledGreyTextField, StyledGridContainer, StyledTableCell } from './BillOfMaterials.styles';
import { BillOfMaterialType, FontStyleProps, PartTypeSearchData } from '../../../@types';
import { COLORS } from '../../../configs/colors';
import { PartTypeCategory, SerialisedType } from '../../../configs/enums';
import { useSearchChildPartTypesByClientAndContract } from '../../../queries/live-search-query';
import { getSerialised } from '../../../utils/part-type';
import { Accordian } from '../../atoms/Accordian';
import { Button, ButtonColorProps, ButtonVariantProps } from '../../atoms/Button';
import { LiveSearchBox, LiveSearchListItem } from '../../atoms/LiveSearchBox';
import { TextField, TextFieldTypeProps } from '../../atoms/TextField';
import { Typography, TypographyVariantProps } from '../../atoms/Typography';
import { LocationSearchItem } from '../../molecules/LocationSearchItem';

export const BillOfMaterials: React.FC<BillOfMaterialsProps> = ({
  billOfMaterialsData,
  parentPartTypeApplicableNodeIds,
  parentPartTypeId,
  parentPartTypeCategoryCode,
  onSave,
  onDelete
}) => {
  const [formData, setFormData] = useState<BillOfMaterialType[]>([]);
  const [textBoxClick, setTextBoxClick] = useState<boolean>(false);
  const [samePartTypeError, setSamePartTypeError] = useState<string>('');

  const searchChildPartTypesByClientAndContract = useSearchChildPartTypesByClientAndContract(false);
  
  useEffect(() => {
    if (billOfMaterialsData) {
      const updatedFormData = [ ...formData ];
      for (const bom of billOfMaterialsData) {
        let matchingBOMConfig = updatedFormData.find((form) => form?.childPartTypeId === bom?.childPartTypeId);
        if (matchingBOMConfig) {
          matchingBOMConfig = {
            ...bom,
            childParentPartTypeId: bom?.childParentPartTypeId || undefined,
            parentHierarchy: bom?.parentHierarchy || '',
            depth: bom?.depth || 0
          };
        } else {
          updatedFormData.push(bom);
        }
      }
      setFormData(updatedFormData);
    }
  }, [billOfMaterialsData]);
    
  const addNewForm = (event: SyntheticEvent) => {
    event.stopPropagation();
    setFormData([...formData, { 
      childPartTypeName: '',
      serialised: '',
      quantity: 0,
      depth: 0
    }
    ]);
  };

  const addNewChild = (event: SyntheticEvent, index: number, childParentPartTypeId?: number) => {
    event.stopPropagation();
    const currentDepth = formData?.find((partType, recordIndex) => index === recordIndex && partType?.childPartTypeId === childParentPartTypeId)?.depth || 0;
    const currentHierarchy = formData?.find((partType, recordIndex) => index === recordIndex && partType?.childPartTypeId === childParentPartTypeId)?.parentHierarchy || '';
    const updatedFormData = [ ...formData ];
    let insertIndex = index === (formData?.length - 1) ? formData?.length :
      formData?.findIndex((record, recordIndex) => {
        if (recordIndex > index && record?.depth !== undefined && record?.depth <= currentDepth) return true;
        else return false;
      });
    if (insertIndex === -1) {
      insertIndex = formData?.length;
    }

    updatedFormData.splice(insertIndex, 0, {
      childPartTypeName: '',
      serialised: '',
      quantity: 0,
      childParentPartTypeId,
      parentHierarchy: currentHierarchy ? `${currentHierarchy},${childParentPartTypeId}` : `${childParentPartTypeId}`,
      depth: currentDepth + 1
    });
    setFormData(updatedFormData);
  };
  
  return (
    <Accordian title={
      <Grid container justifyContent="space-between" width="100%">
        <Grid>
          <Typography>Bill of Materials</Typography>
        </Grid>
        {parentPartTypeCategoryCode === PartTypeCategory.Kit &&
          <Grid mr={2}>
            <Button variant={ButtonVariantProps.Secondary} onClick={(e) => addNewForm(e)}>
              Add
            </Button>              
          </Grid>
        }
      </Grid>
    } defaultExpanded={true}>
      <StyledGridContainer container>
        {[PartTypeCategory.Serialised, PartTypeCategory.Consumable].includes(parentPartTypeCategoryCode as PartTypeCategory) ?
          <Typography variant={TypographyVariantProps.Body2} fontStyle={FontStyleProps.Italic}>
            Previous Bill of Materials (BOM) defined for this Part Type. If experiencing issues with this part type, please reach out to CMS Support.
          </Typography>
          :
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell colSpan={2}>Child Part Type</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {formData?.map((data, index) => 
                  <TableRow key={index}>
                    <StyledTableCell width="40%" depth={data.depth}>
                      {billOfMaterialsData.some((obj) => data.childPartTypeId === obj.childPartTypeId) ?
                        <StyledGreyTextField
                          fullWidth
                          disabled
                          value={data.childPartTypeName}
                        />
                        :
                        <LiveSearchBox
                          title="Child Part Type *"
                          timeOffset={400}
                          value={data.childPartTypeName || ''}
                          onClearValue={() => {
                            const newData = formData.map((item, i) => index === i ? { ...item, childPartTypeId: NaN, childPartTypeName: '' } : item);
                            setFormData(newData);
                            setSamePartTypeError('');
                          }}
                          renderItem={(props: any, option: any) => { 
                            return (
                              <LiveSearchListItem {...props}>
                                <LocationSearchItem data={option} />
                              </LiveSearchListItem>
                            );
                          }}
                          onClick={() => setTextBoxClick(true)}
                          onChange={(partTypeSearchData: PartTypeSearchData) => {
                            const childPartTypeExistsInBOM = formData?.some((obj) => partTypeSearchData?.id === obj?.childPartTypeId);
                            if (partTypeSearchData && childPartTypeExistsInBOM) {
                              setSamePartTypeError(index.toString());
                              return;
                            }

                            const currentHierarchy = formData[index]?.parentHierarchy ? `${formData[index]?.parentHierarchy},${formData[index]?.childPartTypeId}` : `${formData[index]?.childPartTypeId}`;
                            const newData = formData?.map((item, i) =>
                              index === i ? { ...item, childPartTypeId: partTypeSearchData?.id, childPartTypeName: partTypeSearchData?.name, serialised: getSerialised(partTypeSearchData?.categoryCode) } :
                                item)?.filter((partType) => !partType.parentHierarchy?.includes(currentHierarchy));
                            setFormData(newData);
                            samePartTypeError && setSamePartTypeError('');
                          }}
                          onApiInvoke={async (name: string) => {
                            if (textBoxClick && name) {
                              const childPartTypes = await searchChildPartTypesByClientAndContract.mutateAsync({
                                name: name,
                                applicableNodeIds: parentPartTypeApplicableNodeIds,
                                categoryCode: 'any'
                              });

                              return childPartTypes.filter((childPartType) => childPartType?.id !== parentPartTypeId);
                            } else {
                              return [];
                            }
                          }}
                        />
                      }
                      {samePartTypeError === index.toString() &&
                        <Typography variant={TypographyVariantProps.Body2} fontStyle={FontStyleProps.Italic} textColor={COLORS.Red}>
                          Child Part Type already exists in the BOM
                        </Typography>
                      }
                    </StyledTableCell>
                    <StyledTableCell width="10%">
                      {data.serialised}
                    </StyledTableCell>
                    {[SerialisedType.Serialised, SerialisedType.Kit].includes(data.serialised as SerialisedType) ?
                      <StyledTableCell/>
                      :
                      <StyledTableCell>
                        <TextField
                          fullWidth
                          required
                          type={TextFieldTypeProps.Number}
                          placeholder="Quantity *"
                          value={data.quantity}
                          inputProps={{ min: 1 }}
                          onChange={(quantity) => {
                            const newData = formData.map((formDataItem, i) => index === i ? { ...formDataItem, quantity: parseInt(quantity) } : formDataItem);
                            setFormData(newData);
                          }}
                        />
                      </StyledTableCell>
                    }
                    <StyledTableCell align="center">
                      <Button
                        variant={ButtonVariantProps.Secondary}
                        color={ButtonColorProps.Success}
                        onClick={(event) => addNewChild(event, index, data?.childPartTypeId)}
                        disabled={!data.childPartTypeId}
                      >
                        Add Child
                      </Button>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Button
                        variant={ButtonVariantProps.Secondary}
                        color={ButtonColorProps.Success}
                        onClick={() => onSave(data, !billOfMaterialsData.some((obj) => data.childPartTypeId === obj.childPartTypeId))}
                        disabled={!data?.childPartTypeId}
                      >
                      Save
                      </Button>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <IconButton color="error" onClick={() => {
                        const currentHierarchy = formData[index]?.parentHierarchy ? `${formData[index]?.parentHierarchy},${formData[index]?.childPartTypeId}` : `${formData[index]?.childPartTypeId}`;
                        const newData = formData?.filter((obj, i) => index !== i && !obj.parentHierarchy?.includes(currentHierarchy));
                        setFormData(newData);
                        if (billOfMaterialsData.some((obj) => data.childPartTypeId === obj.childPartTypeId)) {
                          const deletedConfig = formData[index];
                          deletedConfig && onDelete(deletedConfig);
                        }
                      }}>
                        {billOfMaterialsData.some((obj) => data.childPartTypeId === obj.childPartTypeId) ?
                          <Delete/> : <CloseRounded />
                        }
                      </IconButton>
                    </StyledTableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        }
      </StyledGridContainer>
    </Accordian>
  );
};
